import { el } from '../lib/dom.js';
import { loadBlocks, sendMessage } from '../lib/content.js';
import { PAGES, visiblePages, isHidden } from '../lib/pages.js';
import { pageMeta } from '../lib/seo.js';
import { renderPage, NEEDS } from './site-view.js';

export { PAGES, visiblePages, isHidden };
import { navigate } from '../main.js';

// The markup lives in site-view.js, shared with the build's prerender; this
// module loads the content and wires up the parts that move.

// Auto-advances like the design, but pauses on hover and honours
// prefers-reduced-motion so it is not a distraction.
function wireCarousel(root, slides) {
  const track = root.querySelector('.carousel-track');
  const capTitle = root.querySelector('.carousel-cap b');
  const capText = root.querySelector('.carousel-cap span');
  const dots = [...root.querySelectorAll('.dots button')];
  let index = 0;
  let timer = null;

  function show(next) {
    index = (next + slides.length) % slides.length;
    track.style.transform = `translateX(-${index * 100}%)`;
    capTitle.textContent = slides[index].title ?? '';
    capText.textContent = slides[index].caption ?? '';
    dots.forEach((d, i) => {
      d.replaceChildren();
      if (i === index) d.appendChild(el('<i></i>'));
    });
  }

  function start() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    stop();
    timer = setInterval(() => show(index + 1), 5200);
  }
  function stop() {
    if (timer) clearInterval(timer);
    timer = null;
  }

  root.querySelector('.prev').addEventListener('click', () => { show(index - 1); start(); });
  root.querySelector('.next').addEventListener('click', () => { show(index + 1); start(); });
  dots.forEach((d, i) => d.addEventListener('click', () => { show(i); start(); }));

  const carousel = root.querySelector('.carousel');
  carousel.addEventListener('mouseenter', stop);
  carousel.addEventListener('mouseleave', start);
  // The interval would otherwise keep firing against a detached DOM.
  root.addEventListener('jhf:teardown', stop);

  show(0);
  start();
}

function wireContact(node) {
  const form = node.querySelector('form');
  const ok = node.querySelector('.note.ok');
  const bad = node.querySelector('.note.bad');
  const button = form.querySelector('button');

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    ok.hidden = true;
    bad.hidden = true;

    const values = Object.fromEntries(new FormData(form));
    if (!values.name?.trim() || !values.contact?.trim()) {
      bad.textContent = 'Please give your name and a way to reach you.';
      bad.hidden = false;
      return;
    }

    button.disabled = true;
    try {
      await sendMessage(values);
      form.reset();
      ok.hidden = false;
    } catch (error) {
      console.error(error);
      bad.textContent = 'Could not send just now. Please try again, or write to us on Instagram.';
      bad.hidden = false;
    } finally {
      button.disabled = false;
    }
  });
}

/* ---------------- entry ---------------- */

export default async function renderSite(root, { page = 'home', eventId = null, notFound = false, hydrate = null } = {}) {
  // Every page needs settings, so it is fetched together with the rest rather
  // than in a round trip of its own: a lone first read pays the whole
  // Firestore connection handshake and can trip the read timeout, leaving the
  // page on seed settings while its other blocks come back live.
  const data = await loadBlocks(...NEEDS[page]);
  if (isHidden(data.settings, page)) {
    navigate('/', { replace: true });
    return null;
  }

  const fresh = el(renderPage({ page, eventId, data }));

  // On first load the prerendered page is already on screen. If the live
  // content still renders identically, keep that DOM: replacing it would
  // replay every entrance animation and reset the scroll for nothing. Only
  // when content changed since the last deploy is it swapped out.
  const shell = hydrate?.isEqualNode(fresh) ? hydrate : fresh;
  root.adopted = shell === hydrate;

  if (page === 'home' && data.home.slides?.length) wireCarousel(shell, data.home.slides);
  if (page === 'contact') wireContact(shell);

  // Handed to the router rather than applied here: this render may yet be
  // overtaken, and a stale page must not leave its title behind.
  const unknownEvent = page === 'events' && eventId && !(data.events.items ?? []).some((e) => e.id === eventId);
  root.seo = pageMeta({ page, eventId, notFound: notFound || unknownEvent, data });

  if (!root.adopted) root.appendChild(shell);
  return () => shell.dispatchEvent(new CustomEvent('jhf:teardown'));
}
